import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CalendarRange, Users, BookOpen, XCircle, Sparkles, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const ML = { arabe: "Arabe", coran: "Coran", education_musulmane: "Éd. Musulmane" };

const KIND_LABELS = {
  vacances: "Vacances",
  reunion: "Réunion parents-profs",
  cours_supplementaire: "Cours supplémentaire",
  cours_annule: "Cours annulé",
  autre: "Autre",
};

const KIND_META = {
  vacances: { icon: CalendarRange, color: "bg-[#F4F1EB] text-[#5c6374] border-[#EAE6DF]" },
  reunion: { icon: Users, color: "bg-[#FBE8DC] text-[#C86444] border-[#C86444]/30" },
  cours_supplementaire: { icon: BookOpen, color: "bg-[#E0F2E9] text-[#1E7A4D] border-[#1E7A4D]/30" },
  cours_annule: { icon: XCircle, color: "bg-[#FBE5E4] text-[#B0413E] border-[#B0413E]/30" },
  autre: { icon: Sparkles, color: "bg-[#E0E7FF] text-[#1E3A8A] border-[#1E3A8A]/30" },
};

/**
 * Onglet "Événements" — fusion vacances + réunions + cours supplémentaires/annulés.
 * - vacances => /api/vacations
 * - autres types (reunion, cours_supplementaire, cours_annule, autre) => /api/meetings avec kind
 */
export default function EventsTab({ vacations, meetings, courses, groups, onSaved }) {
  const events = useMemo(() => {
    const vacs = (vacations || []).map((v) => ({
      kind: "vacances",
      id: v.id,
      title: v.name,
      date: v.start_date,
      end_date: v.end_date,
      raw: v,
    }));
    const others = (meetings || []).map((m) => ({
      kind: m.kind || "reunion",
      id: m.id,
      title: m.title,
      date: m.date,
      start_time: m.start_time,
      end_time: m.end_time,
      location: m.location,
      description: m.description,
      target_course_id: m.target_course_id,
      custom_label: m.custom_label,
      raw: m,
    }));
    return [...vacs, ...others].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  }, [vacations, meetings]);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => (e.end_date || e.date) >= today);
  const past = events.filter((e) => (e.end_date || e.date) < today);

  return (
    <div className="space-y-6" data-testid="events-tab">
      <div className="bg-white border border-[#EAE6DF] rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-[Outfit] font-semibold text-base text-[#1A2547]">Événements du calendrier</h3>
          <p className="text-xs text-[#5c6374] mt-1">Vacances, réunions, cours supplémentaires, cours annulés… tous visibles sur le planning des parents et enseignants.</p>
        </div>
        <EventDialog courses={courses} groups={groups} onSaved={onSaved} />
      </div>

      <EventsList title={`À venir (${upcoming.length})`} list={upcoming} courses={courses} groups={groups} onSaved={onSaved} testid="events-upcoming" />
      {past.length > 0 && <EventsList title={`Passés (${past.length})`} list={past} courses={courses} groups={groups} onSaved={onSaved} testid="events-past" muted />}
    </div>
  );
}

function EventsList({ title, list, courses, groups, onSaved, testid, muted }) {
  if (list.length === 0) {
    return (
      <div className="bg-white border border-[#EAE6DF] rounded-2xl">
        <div className="px-5 py-3 border-b border-[#EAE6DF] flex items-center justify-between">
          <h4 className="text-sm font-[Outfit] font-semibold text-[#1A2547]">{title}</h4>
        </div>
        <div className="p-10 text-center text-[#5c6374] text-sm">Aucun événement.</div>
      </div>
    );
  }
  return (
    <div className={`bg-white border border-[#EAE6DF] rounded-2xl ${muted ? "opacity-80" : ""}`} data-testid={testid}>
      <div className="px-5 py-3 border-b border-[#EAE6DF]">
        <h4 className="text-sm font-[Outfit] font-semibold text-[#1A2547]">{title}</h4>
      </div>
      <ul className="divide-y divide-[#EAE6DF]">
        {list.map((e) => <EventRow key={`${e.kind}-${e.id}`} event={e} courses={courses} groups={groups} onSaved={onSaved} />)}
      </ul>
    </div>
  );
}

function EventRow({ event, courses, groups, onSaved }) {
  const Meta = KIND_META[event.kind] || KIND_META.autre;
  const Icon = Meta.icon;
  const fmtFrDate = (s) => {
    if (!s) return "";
    try {
      return new Date(s).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    } catch { return s; }
  };
  const course = event.target_course_id ? (courses || []).find((c) => c.id === event.target_course_id) : null;
  const group = course ? (groups || []).find((g) => g.id === course.group_id) : null;

  const remove = async () => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    try {
      if (event.kind === "vacances") await api.delete(`/vacations/${event.id}`);
      else await api.delete(`/meetings/${event.id}`);
      toast.success("Événement supprimé");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <li className="px-5 py-3.5 flex flex-wrap items-start gap-3" data-testid={`event-row-${event.kind}-${event.id}`}>
      <div className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${Meta.color}`}>
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-[Outfit] font-semibold text-sm text-[#1A2547]">{event.title}</span>
          <Badge className={`${Meta.color} hover:${Meta.color} rounded-full text-[10px] border`}>
            {event.kind === "autre" && event.custom_label ? event.custom_label : KIND_LABELS[event.kind]}
          </Badge>
        </div>
        <div className="text-xs text-[#5c6374] mt-0.5">
          {event.kind === "vacances"
            ? <>Du <strong>{fmtFrDate(event.date)}</strong> au <strong>{fmtFrDate(event.end_date)}</strong></>
            : <>{fmtFrDate(event.date)} · {event.start_time}–{event.end_time}{event.location ? ` · ${event.location}` : ""}</>
          }
          {course && (
            <span className="ml-1 text-[#5c6374]">
              — <strong>{group?.name || "?"}</strong> · {ML[course.matiere] || course.matiere}
            </span>
          )}
        </div>
        {event.description && (
          <div className="text-[11px] text-[#5c6374] mt-1 italic">« {event.description} »</div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {event.kind !== "vacances" && (
          <EventDialog
            courses={courses}
            groups={groups}
            onSaved={onSaved}
            existing={event}
          />
        )}
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={remove} data-testid={`delete-event-${event.id}`}>
          <Trash2 className="w-4 h-4 text-[#B0413E]" />
        </Button>
      </div>
    </li>
  );
}

function EventDialog({ courses, groups, onSaved, existing }) {
  const [open, setOpen] = useState(false);
  const isEdit = !!existing;

  const empty = {
    kind: "reunion",
    title: "",
    date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    location: "",
    description: "",
    target_course_id: "",
    custom_label: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm({
          kind: existing.kind || "reunion",
          title: existing.title || "",
          date: existing.date || "",
          end_date: existing.end_date || "",
          start_time: existing.start_time || "",
          end_time: existing.end_time || "",
          location: existing.location || "",
          description: existing.description || "",
          target_course_id: existing.target_course_id || "",
          custom_label: existing.custom_label || "",
        });
      } else setForm(empty);
    }
    // eslint-disable-next-line
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Quand le type change, suggérer un titre par défaut + champ cours requis
  const onKindChange = (k) => {
    const defaultTitle = {
      vacances: "Vacances scolaires",
      reunion: "Réunion parents-profs",
      cours_supplementaire: "Cours supplémentaire",
      cours_annule: "Cours annulé",
      autre: "",
    };
    setForm((f) => ({
      ...f,
      kind: k,
      title: f.title || defaultTitle[k] || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.kind === "vacances") {
        if (!form.date || !form.end_date) throw new Error("Dates début et fin requises");
        if (form.end_date < form.date) throw new Error("La date de fin doit être après le début");
        await api.post("/vacations", {
          name: form.title || "Vacances",
          start_date: form.date,
          end_date: form.end_date,
        });
      } else {
        if (!form.date || !form.start_time || !form.end_time) throw new Error("Date et horaires requis");
        if (["cours_annule", "cours_supplementaire"].includes(form.kind) && !form.target_course_id) {
          throw new Error("Sélectionnez le cours concerné");
        }
        const payload = {
          title: form.title || KIND_LABELS[form.kind],
          date: form.date,
          start_time: form.start_time,
          end_time: form.end_time,
          location: form.location || null,
          description: form.description || null,
          kind: form.kind,
          target_course_id: form.target_course_id || null,
          custom_label: form.kind === "autre" ? (form.custom_label || null) : null,
        };
        if (isEdit) {
          await api.put(`/meetings/${existing.id}`, payload);
        } else {
          await api.post("/meetings", payload);
        }
      }
      toast.success(isEdit ? "Événement mis à jour" : "Événement ajouté");
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail ? formatApiError(err.response.data.detail) : (err.message || "Erreur"));
    } finally {
      setSaving(false);
    }
  };

  const needsCourse = ["cours_annule", "cours_supplementaire"].includes(form.kind);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`edit-event-${existing.id}`}>
            <Pencil className="w-4 h-4 text-[#1E3A8A]" />
          </Button>
        ) : (
          <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="add-event-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Ajouter un événement
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifier l'événement" : "Nouvel événement au calendrier"}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-xs">Cet événement sera visible dans le planning de l'administration, des enseignants et des parents.</DialogDescription>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label className="text-sm">Type d'événement *</Label>
            <Select value={form.kind} onValueChange={onKindChange} disabled={isEdit}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="event-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vacances">Vacances</SelectItem>
                <SelectItem value="reunion">Réunion parents-profs</SelectItem>
                <SelectItem value="cours_supplementaire">Cours supplémentaire</SelectItem>
                <SelectItem value="cours_annule">Cours annulé</SelectItem>
                <SelectItem value="autre">Autre (libellé libre)</SelectItem>
              </SelectContent>
            </Select>
            {isEdit && form.kind !== "vacances" && (
              <p className="text-[10px] text-[#8a92a3] mt-1">Pour changer de type, supprimez puis recréez l'événement.</p>
            )}
          </div>

          {form.kind === "autre" && (
            <div>
              <Label className="text-sm">Libellé personnalisé *</Label>
              <Input required value={form.custom_label} onChange={(e) => set("custom_label", e.target.value)} className="rounded-xl mt-1.5" placeholder="Ex: Sortie pédagogique, Aïd…" data-testid="event-custom-label" />
            </div>
          )}

          <div>
            <Label className="text-sm">Intitulé *</Label>
            <Input required value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-xl mt-1.5" data-testid="event-title" />
          </div>

          {needsCourse && (
            <div>
              <Label className="text-sm">Cours concerné *</Label>
              <Select value={form.target_course_id} onValueChange={(v) => set("target_course_id", v)}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="event-course"><SelectValue placeholder="Choisir un cours…" /></SelectTrigger>
                <SelectContent>
                  {(courses || []).map((c) => {
                    const g = (groups || []).find((x) => x.id === c.group_id);
                    return (
                      <SelectItem key={c.id} value={c.id}>
                        {g?.name || "?"} · {ML[c.matiere] || c.matiere} · {c.schedule_day} {c.schedule_time}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          {form.kind === "vacances" ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Date début *</Label>
                <Input required type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="rounded-xl mt-1.5" data-testid="event-date-start" />
              </div>
              <div>
                <Label className="text-sm">Date fin *</Label>
                <Input required type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="rounded-xl mt-1.5" data-testid="event-date-end" />
              </div>
            </div>
          ) : (
            <>
              <div>
                <Label className="text-sm">Date *</Label>
                <Input required type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="rounded-xl mt-1.5" data-testid="event-date" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Heure début *</Label>
                  <Input required type="time" value={form.start_time} onChange={(e) => set("start_time", e.target.value)} className="rounded-xl mt-1.5" data-testid="event-start-time" />
                </div>
                <div>
                  <Label className="text-sm">Heure fin *</Label>
                  <Input required type="time" value={form.end_time} onChange={(e) => set("end_time", e.target.value)} className="rounded-xl mt-1.5" data-testid="event-end-time" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Lieu (optionnel)</Label>
                <Input value={form.location} onChange={(e) => set("location", e.target.value)} className="rounded-xl mt-1.5" placeholder="Ex: Salle polyvalente" data-testid="event-location" />
              </div>
              <div>
                <Label className="text-sm">Description (optionnel)</Label>
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="rounded-xl mt-1.5" placeholder="Précisions, ordre du jour, raison de l'annulation…" />
              </div>
            </>
          )}

          <Button type="submit" disabled={saving} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="event-submit">
            {saving ? "Enregistrement…" : (isEdit ? "Enregistrer les modifications" : "Ajouter au calendrier")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
